import React, { useEffect, useState } from 'react'; 
import { useParams, useNavigate } from 'react-router-dom';
import { GetQuizInstances, AnswerSurvey } from './api';
import './Survey.css';

function Survey() {
    const { QuizInstanceId } = useParams();
    const [quizInfo, setQuizInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [timeRemaining, setTimeRemaining] = useState(0);
    const [answers, setAnswers] = useState([]); // Réponses possibles
    const [selectedAnswer, setSelectedAnswer] = useState(null); // Réponse sélectionnée
    const [questionId, setQuestionId] = useState(""); // ID de la question
    const [timeToAnswer, setTimeToAnswer] = useState(0); // Temps pour répondre
    const [answerStatus, setAnswerStatus] = useState(null);
    const navigate = useNavigate();
    const [currentQuestion, setCurrentQuestion] = useState(null);  

    useEffect(() => {
        async function fetchQuiz() {
            try {
                setLoading(true);
                const response = await GetQuizInstances(QuizInstanceId);
                console.log('Quiz response:', response);

                if (response.success && Array.isArray(response.validInstances)) {
                    const quizInstance = response.validInstances.find(
                        (instance) => instance.instanceId === QuizInstanceId
                    );
                    
                    if (quizInstance) {
                        setQuizInfo({
                            quizId: quizInstance.instanceId,
                            quizDisplayName: quizInstance.displayName,
                            quizVoteExpiration: quizInstance.voteExpiration,
                            quizTimestamp: quizInstance.timestamp,
                            quizTotalExpiration: quizInstance.totalExpiration
                        });
                        
                        // Use the embedded questions directly
                        if (quizInstance.questions && quizInstance.questions.length > 0) {
                            const thisQuestion = quizInstance.questions[0];
                            console.log('Current question:', thisQuestion);
                            setCurrentQuestion(thisQuestion);
                            setAnswers(thisQuestion.choices);
                            setQuestionId(thisQuestion.id || "0");
                            console.log("Answers set:", thisQuestion.choices.map(choice => choice.text || choice.label));
                        } else if (quizInstance.jsonUrl) {
                            // Fallback to JSON URL if questions aren't embedded
                            try {
                                const questionsDataResponse = await fetch(quizInstance.jsonUrl);
                                const questionsData = await questionsDataResponse.json();
                                
                                if (questionsData && questionsData.questions && questionsData.questions.length > 0) {
                                    const thisQuestionData = questionsData.questions[0];
                                    console.log('Question from JSON:', thisQuestionData);
                                    setCurrentQuestion(thisQuestionData);
                                    setAnswers(thisQuestionData.choices);
                                    setQuestionId(thisQuestionData.id || "0");
                                }
                            } catch (jsonError) {
                                console.error('Error fetching questions JSON:', jsonError);
                                throw new Error('Failed to load questions data');
                            }
                        }
                        
                        setTimeToAnswer(quizInstance.defaultTimeToAnswer || 30);
                    } else {
                        throw new Error('Quiz expiré ou introuvable.');
                    }
                } else {
                    throw new Error('La réponse ne contient pas de quiz valide.');
                }
            } catch (err) {
                console.error(err.message);
                setError(err);
            } finally {
                setLoading(false);
            }
        }

        if (QuizInstanceId) {
            fetchQuiz();
        } else {
            console.error("QuizInstanceId est undefined, impossible de récupérer le quiz.");
            setLoading(false);
        }
    }, [QuizInstanceId]);

    useEffect(() => {
        if (quizInfo && quizInfo.quizVoteExpiration) {
            const interval = setInterval(() => {
                const now = new Date();
                const expirationTime = new Date(quizInfo.quizVoteExpiration * 1000);
                const timeDiff = expirationTime - now;

                if (timeDiff <= 0) {
                    clearInterval(interval);
                    setTimeRemaining(0);
                } else {
                    setTimeRemaining(Math.floor(timeDiff / 1000));
                }
            }, 1000);

            return () => clearInterval(interval);
        }
    }, [quizInfo]);

    const handleSelectAnswer = (answer) => {
        setSelectedAnswer(answer);
    };

    const handleAnswerSurvey = async () => {
        if (!quizInfo || !selectedAnswer) {
            alert("Veuillez sélectionner une réponse avant d'envoyer.");
            return;
        }

        try {
            setAnswerStatus('loading');
            console.log(  
                `Quiz Instance ID: ${QuizInstanceId},   
                 Question ID: ${questionId},   
                 Time to Answer: ${timeToAnswer},   
                 Selected Answer: ${selectedAnswer}`  
            );
            
            // Find the choice ID that corresponds to the selected label
            let selectedChoiceId = selectedAnswer;
            const choiceWithLabel = answers.find(choice => choice.label === selectedAnswer);
            if (choiceWithLabel && choiceWithLabel.id) {
                selectedChoiceId = choiceWithLabel.id;
            }
            
            const response = await AnswerSurvey(QuizInstanceId, questionId, timeToAnswer, selectedChoiceId);

            if (response.success) {
                setAnswerStatus('success');
                console.log("Réponse envoyée avec succès :", response.data);
                
                // If there's a next question, we could handle it here
                if (response.data && response.data.nextQuestionId) {
                    console.log("Next question ID:", response.data.nextQuestionId);
                    // Could implement multi-question surveys here
                }
            } else {
                throw new Error(response.error || 'Erreur lors de l\'envoi de la réponse.');
            }
        } catch (err) {
            console.error(err.message);
            setAnswerStatus('error');
        }
    };

    const handleExitQuiz = () => {
        navigate('/Accueil');
    };

    if (loading) {
        return (
            <div className="loading-message1">
                Chargement du vote
                <div className="loading-dots">
                    <span>.</span>
                    <span>.</span>
                    <span>.</span>
                </div>
            </div>
        );
    }

    if (error) {
        return <div className="error">Erreur : {error.message || 'Une erreur est survenue.'}</div>;
    }
  
    return (  
        <div className="survey-container">  
            <h2>{quizInfo?.quizDisplayName}</h2>  
            <p>{quizInfo ? "Voici votre quiz !" : "Quiz expiré."}</p>  
              
            {/* Display the current question */}  
            {currentQuestion && (  
                <div className="question-container">  
                    <h3>{currentQuestion.question}</h3>  
                </div>  
            )}  
      
            <div className="display-minutes-container">  
                <div className="time-display">  
                    {Math.floor(timeRemaining / 60)}:{(timeRemaining % 60) < 10 ? `0${timeRemaining % 60}` : timeRemaining % 60}  
                </div>  
                <div className="display-label">Temps restant</div>  
            </div>  
      
            {/* Display the choices */}  
            {answers.length > 0 ? (  
                <div className="answers-container">  
                   {answers.map((choice) => (  
                        <div  
                            key={choice.id}  // Use the unique `id` from each choice object  
                            className={`answer-option ${selectedAnswer === choice.label ? 'selected' : ''}`}  
                            onClick={() => handleSelectAnswer(choice.label)}  
                        >  
                            {choice.label} 
                        </div>  
                    ))}   
                </div>  
            ) : (  
                <p>Aucune réponse disponible pour cette question.</p>  
            )}  
      
            <button className="answer-button" onClick={handleAnswerSurvey}>  
                Répondre au sondage  
            </button>  
            {answerStatus === 'loading' && <p>Envoi en cours...</p>}  
            {answerStatus === 'success' && <p>Réponse envoyée avec succès !</p>}  
            {answerStatus === 'error' && <p>Erreur lors de l'envoi de la réponse.</p>}  
      
            <button className="exit-button" onClick={handleExitQuiz}>  
                Quitter l'événement  
            </button>  
        </div>  
    );  
}

export default Survey;
